import { Pencil, Trash2 } from "lucide-react";

import {
  beerDutyLabel,
  formatScheduleDate,
  homeAwayLabel,
  timeSlotLabel,
  timeSlotName,
} from "../../domain/schedule";
import {
  playoffAvailabilityLabel,
  playoffGameAvailability,
} from "../../domain/playoffSeries";
import type { ScheduleForecastState } from "../../domain/weather";
import type {
  PlayoffGameAvailability,
  PlayoffSeriesModel,
} from "../../shared/types/contracts";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { GameWeather } from "./GameWeather";
import {
  scheduleResultBadgeVariant,
  scheduleResultLabel,
} from "./schedulePresentation";
import type { ScheduleEntry } from "./scheduleTypes";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { Spinner } from "../../shared/ui/spinner";

interface PlayoffSeriesListProps {
  teamName: string;
  series: PlayoffSeriesModel[];
  entries: ScheduleEntry[];
  readOnly: boolean;
  onOpen: (entry: ScheduleEntry, availability: PlayoffGameAvailability) => void;
  onEditGame: (entry: ScheduleEntry) => void;
  onEditSeries: (series: PlayoffSeriesModel) => void;
  onDeleteSeries: (series: PlayoffSeriesModel) => void;
  weatherByEntryId: Record<string, ScheduleForecastState>;
  onRetryWeather: () => void;
  actionStatus: IntentStatusView;
  weatherRefreshing?: boolean;
}

export function PlayoffSeriesList({
  teamName,
  series,
  entries,
  readOnly,
  onOpen,
  onEditGame,
  onEditSeries,
  onDeleteSeries,
  weatherByEntryId,
  onRetryWeather,
  actionStatus,
  weatherRefreshing = false,
}: PlayoffSeriesListProps) {
  const entriesById = new Map(entries.map((entry) => [entry.id, entry]));
  return (
    <section aria-labelledby="playoffs-heading">
      <h2
        id="playoffs-heading"
        className="mb-2 font-mono text-sm font-bold tracking-[0.1em] text-warning uppercase"
      >
        PLAYOFFS
      </h2>
      {series.length ? (
        <div className="grid gap-3">
          {series.map((playoffSeries) => (
            <PlayoffSeriesCard
              key={playoffSeries.id}
              teamName={teamName}
              series={playoffSeries}
              entriesById={entriesById}
              readOnly={readOnly}
              onOpen={onOpen}
              onEditGame={onEditGame}
              onEditSeries={onEditSeries}
              onDeleteSeries={onDeleteSeries}
              weatherByEntryId={weatherByEntryId}
              onRetryWeather={onRetryWeather}
              actionStatus={actionStatus}
              weatherRefreshing={weatherRefreshing}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-[var(--radius-sm)] border border-dashed border-border px-3 py-4 text-sm text-foreground-muted">
          No playoff series scheduled.
        </p>
      )}
    </section>
  );
}

function PlayoffSeriesCard({
  teamName,
  series,
  entriesById,
  readOnly,
  onOpen,
  onEditGame,
  onEditSeries,
  onDeleteSeries,
  weatherByEntryId,
  onRetryWeather,
  actionStatus,
  weatherRefreshing = false,
}: Omit<PlayoffSeriesListProps, "series" | "entries"> & {
  series: PlayoffSeriesModel;
  entriesById: Map<string, ScheduleEntry>;
}) {
  return (
    <Card className="overflow-hidden border-warning/40">
      <CardContent className="grid gap-3 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {series.title.trim().toLowerCase() !== "playoffs" ? (
                <strong className="font-mono text-sm tracking-[0.08em] text-warning uppercase">
                  {series.title}
                </strong>
              ) : null}
              <Badge variant="neutral">BEST OF {series.bestOf}</Badge>
              <SeriesStatusBadge series={series} />
            </div>
            <p className="mt-1 mb-0 text-sm font-bold">
              {teamName} {series.ourWins}–{series.opponentWins}{" "}
              {series.opponentName}
            </p>
          </div>
          {series.status === "scheduled" && !readOnly ? (
            <div className="flex shrink-0 gap-1">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Edit playoff series"
                onClick={() => onEditSeries(series)}
              >
                <Pencil aria-hidden="true" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Delete playoff series"
                onClick={() => onDeleteSeries(series)}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            </div>
          ) : null}
        </div>
        <ol className="m-0 grid list-none gap-2 p-0">
          {series.scheduleIds.map((scheduleId, index) => {
            const entry = entriesById.get(scheduleId);
            if (!entry) {
              return (
                <li key={scheduleId} className="text-sm text-danger">
                  Game {index + 1} is unavailable.
                </li>
              );
            }
            return (
              <PlayoffGameRow
                key={scheduleId}
                series={series}
                entry={entry}
                index={index}
                readOnly={readOnly}
                onOpen={onOpen}
                onEditGame={onEditGame}
                weatherState={weatherByEntryId[entry.id]}
                onRetryWeather={onRetryWeather}
                actionStatus={actionStatus}
                weatherRefreshing={weatherRefreshing}
              />
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function PlayoffGameRow({
  series,
  entry,
  index,
  readOnly,
  onOpen,
  onEditGame,
  weatherState,
  onRetryWeather,
  actionStatus,
  weatherRefreshing = false,
}: {
  series: PlayoffSeriesModel;
  entry: ScheduleEntry;
  index: number;
  readOnly: boolean;
  onOpen: PlayoffSeriesListProps["onOpen"];
  onEditGame: PlayoffSeriesListProps["onEditGame"];
  weatherState: ScheduleForecastState | undefined;
  onRetryWeather: () => void;
  actionStatus: IntentStatusView;
  weatherRefreshing?: boolean;
}) {
  const gameNumber = entry.seriesGameNumber ?? index + 1;
  const availability =
    entry.seriesAvailability ?? playoffGameAvailability(series, gameNumber);
  const availabilityLabel =
    entry.status === "scheduled"
      ? playoffAvailabilityLabel(series, gameNumber)
      : null;
  const locked = entry.status === "scheduled" && availability !== "required";
  const notNeeded =
    availability === "not_needed" && entry.status === "scheduled";
  const selecting = actionStatus.isPending(actionKeys.scheduleSelect(entry.id));
  const selectionUnknown =
    actionStatus.phaseFor(actionKeys.scheduleSelect(entry.id)) ===
    "outcome_unknown";
  const selectionPending =
    actionStatus.countFor(actionKeys.scheduleSelectGroup) > 0;
  return (
    <li
      className={`rounded-[var(--radius-sm)] border px-3 py-2 ${notNeeded ? "border-border/60 opacity-60" : "border-border"}`}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="min-w-0 flex-1 text-left disabled:cursor-not-allowed"
          disabled={
            selecting ||
            selectionPending ||
            locked ||
            (readOnly && entry.status !== "completed")
          }
          aria-busy={selecting || undefined}
          onClick={() => onOpen(entry, availability)}
        >
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-muted">
            <strong className="text-foreground">GAME {gameNumber}</strong>
            <span>{formatScheduleDate(entry.scheduledDate)}</span>
            <span>
              {timeSlotLabel(entry.timeSlot)} · {timeSlotName(entry.timeSlot)}
            </span>
            {entry.status === "active" ? (
              <Badge variant="warning">LIVE</Badge>
            ) : null}
            {availabilityLabel ? (
              <Badge variant={notNeeded ? "neutral" : "warning"}>
                {availabilityLabel}
              </Badge>
            ) : null}
          </div>
          <div className="mt-1 text-sm font-bold">
            {entry.isHome ? "vs" : "at"} {entry.opponentName}
          </div>
          {selecting ? (
            <div className="mt-1 inline-flex items-center gap-1 font-mono text-[0.65rem] font-bold text-warning">
              <Spinner decorative className="size-3" />{" "}
              {selectionUnknown ? "CHECKING…" : "SELECTING…"}
            </div>
          ) : null}
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-foreground-muted">
            <span>{entry.field ?? "Field unknown"}</span>
            <span>{homeAwayLabel(entry.isHome)}</span>
            <span>Beer: {beerDutyLabel(entry.beerDuty)}</span>
          </div>
        </button>
        {entry.status === "completed" ? (
          <Badge
            variant={
              entry.result ? scheduleResultBadgeVariant(entry) : "neutral"
            }
          >
            {entry.result ? scheduleResultLabel(entry) : "BOX SCORE"}
          </Badge>
        ) : !notNeeded && !readOnly ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Edit playoff Game ${gameNumber}`}
            onClick={() => onEditGame(entry)}
          >
            <Pencil aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {entry.status !== "completed" && !notNeeded && !readOnly ? (
        <GameWeather
          state={weatherState}
          onRetry={onRetryWeather}
          refreshing={weatherRefreshing}
        />
      ) : null}
    </li>
  );
}

function SeriesStatusBadge({ series }: { series: PlayoffSeriesModel }) {
  if (series.winner === "us") {
    return <Badge variant="success">SERIES WON</Badge>;
  }
  if (series.winner === "them") {
    return <Badge variant="danger">SERIES LOST</Badge>;
  }
  if (series.status === "in_progress") {
    return <Badge variant="warning">IN PROGRESS</Badge>;
  }
  return <Badge variant="neutral">SCHEDULED</Badge>;
}
